import useVuelidate from "@vuelidate/core";
import { storeToRefs } from "pinia";
import { helpers, required, minValue } from "@vuelidate/validators";
import { isBefore, add } from "date-fns";
import { HabilitationRenewStatusEnum, SettingScopesEnum, HabilitationValidationStatusEnum } from "@simbelapp/order-sdk";
import type { IIndexHeader } from "~/utils/interfaces/table-interfaces";
import { HabilitationStatusEnum } from "~/utils/enums/habilitations.enums";
import { useCreateUserHabilitationsStore } from "~/store/users-habilitations/create-user-habilitations.store";
import { useUpdateUserHabilitationsStore } from "~/store/users-habilitations/update-user-habilitation.store";
import type { IFooterActions } from "~/utils/interfaces/common-interfaces";
import { ProjectTypeEnum } from "~/utils/enums/project.enums";
import type { ICompanyUsersSDK } from "~/utils/interfaces/users-interfaces";
import { useUsersHabilitationsStore } from "~/store/users-habilitations/users-habilitations.store";
import { useCreateProjectStore } from "~/store/project/create-project.store";
import { ItemType } from "~/utils/enums/common.enums";

export const useUsersHabilitations = () => {
  const headers = useState<IIndexHeader[]>(() => [
    {
      name: "Employé",
      sort: true,
      key: "lastname",
      minWidthPx: 246,
      maxWidthFr: 2,
      visible: true,
      scope: SettingScopesEnum.HabilitationsTab,
    },
    {
      name: "Habilitation",
      sort: true,
      key: "habilitation_name",
      minWidthPx: 297,
      maxWidthFr: 5,
      visible: true,
      scope: SettingScopesEnum.HabilitationsTab,
    },
    {
      name: "Validité",
      sort: true,
      key: "habilitation_status",
      minWidthPx: 160,
      maxWidthFr: 2,
      visible: true,
      scope: SettingScopesEnum.HabilitationsTab,
    },
    {
      name: "Expire le",
      sort: true,
      key: "expiration_date",
      minWidthPx: 110,
      maxWidthFr: 1,
      visible: true,
      scope: SettingScopesEnum.HabilitationsTab,
    },
    {
      name: "Certificat",
      sort: true,
      key: "document_link",
      minWidthPx: 123,
      maxWidthFr: 1,
      visible: true,
      scope: SettingScopesEnum.HabilitationsTab,
    },
    {
      name: "À renouveler",
      sort: true,
      key: "to_renew",
      minWidthPx: 117,
      maxWidthFr: 1,
      visible: true,
      scope: SettingScopesEnum.HabilitationsTab,
    },
    {
      name: "Statut renouvellement",
      sort: true,
      key: "renew_status",
      minWidthPx: 160,
      maxWidthFr: 1,
      visible: true,
      scope: SettingScopesEnum.HabilitationsTab,
    },
    {
      name: "Prévu le",
      sort: true,
      key: "renewal_date",
      minWidthPx: 141,
      maxWidthFr: 1,
      visible: true,
      scope: SettingScopesEnum.HabilitationsTab,
    },
    {
      name: "Site",
      sort: true,
      key: "site",
      minWidthPx: 120,
      maxWidthFr: 1,
      visible: false,
      scope: SettingScopesEnum.HabilitationsTab,
    },
    {
      name: "Statut utilisateur",
      sort: true,
      key: "user_status",
      minWidthPx: 150,
      maxWidthFr: 2,
      visible: false,
      scope: SettingScopesEnum.HabilitationsTab,
    },
    {
      name: "Manager",
      sort: true,
      key: "manager_lastname",
      minWidthPx: 200,
      maxWidthFr: 1,
      visible: false,
      scope: SettingScopesEnum.HabilitationsTab,
    },
    {
      name: "Entité légale",
      sort: true,
      key: "legal_entity_name",
      minWidthPx: 220,
      maxWidthFr: 1,
      visible: false,
      scope: SettingScopesEnum.HabilitationsTab,
    },
    {
      name: "Poste",
      sort: true,
      key: "job",
      minWidthPx: 180,
      maxWidthFr: 1,
      visible: false,
      scope: SettingScopesEnum.HabilitationsTab,
    },
    {
      name: "Date de début de contrat",
      sort: true,
      key: "contract_start_date",
      minWidthPx: 120,
      maxWidthFr: 1,
      visible: false,
      scope: SettingScopesEnum.HabilitationsTab,
    },
    {
      name: "Direction",
      sort: true,
      key: "business_unit",
      minWidthPx: 180,
      maxWidthFr: 1,
      visible: false,
      scope: SettingScopesEnum.HabilitationsTab,
    },
  ]);

  const footersActions: IFooterActions[] = [
    {
      name: "Ajouter au plan",
      logoName: "create",
      action: showUserManagement,
    },
  ];

  async function showUserManagement(selectedCount: number) {
    const createProjectCoreStore = useCreateProjectStore();
    if (!createProjectCoreStore.canAddToPlan(selectedCount)) return;
    const { project } = storeToRefs(createProjectCoreStore);
    const usersHabilitationsStore = useUsersHabilitationsStore();

    createProjectCoreStore.openProjectModal();
    project.value.projectType = ProjectTypeEnum.NEW_PROJECT;
    project.value.sourceType = ItemType.HABILITATION;

    if (usersHabilitationsStore.excludedUsersIdList.length > 0 || !usersHabilitationsStore.selectedUsersIdList.length)
      await usersHabilitationsStore.fetchNotExcludedUserHabilitation(usersHabilitationsStore.excludedUsersIdList);
    const uniqueUsersMap = new Map();
    const selectedItems = usersHabilitationsStore.selectedUsersIdList.filter((item: ICompanyUsersSDK) => {
      item.displayValue = item.displayValue ?? item.userDisplayValue;
      if (!uniqueUsersMap.has(item.userId)) {
        uniqueUsersMap.set(item.userId, true);
        return true;
      }
      return false;
    });

    project.value.users.usersId = selectedItems.map((u) => {
      return {
        userId: u.userId,
        id: project.value.projectId,
        firstname: u.firstname ?? null,
        lastname: u.lastname ?? null,
        email: u.email ?? null,
        displayValue: u.displayValue ?? null,
      };
    });
  }

  function statusMapping(value: string): { name: string; value: string; type: string } | undefined {
    return [
      {
        name: HabilitationStatusEnum.EXPIRED,
        value: "Expirée",
        type: "light_red",
      },
      {
        name: HabilitationStatusEnum.UNKNOWN,
        value: "Non connue",
        type: "grey",
      },
      {
        name: HabilitationStatusEnum.VALID,
        value: "Valide",
        type: "light_green",
      },
      {
        name: HabilitationStatusEnum.EXPIRING_SOON,
        value: "Expire bientôt",
        type: "light_orange",
      },
      {
        name: HabilitationStatusEnum.COMING,
        value: "À venir",
        type: "light_blue",
      },
      {
        name: HabilitationStatusEnum.MISSING_HABILITATION,
        value: "Manquant",
        type: "grey",
      },
    ].find((x) => x.name === value);
  }

  function renewalStatusMapping(value: string): { name: string; value: string; type: string } | undefined {
    return [
      {
        name: HabilitationRenewStatusEnum.PlannedProject,
        value: "Prévu - brouillon",
        type: "light_blue",
      },
      {
        name: HabilitationRenewStatusEnum.NotPlanned,
        value: "Non prévu",
        type: "light_purple",
      },
      {
        name: HabilitationRenewStatusEnum.PlannedOutsideOfSimbel,
        value: "Prévu - hors Simbel",
        type: "light_blue",
      },
      {
        name: HabilitationRenewStatusEnum.PlannedSession,
        value: "Prévu - validé",
        type: "light_blue",
      },
    ].find((x) => x.name === value);
  }
  function attendeeStatusMapping(value: string): { name: string; value: string; type: string } | undefined {
    return [
      {
        name: HabilitationValidationStatusEnum.Valid,
        value: "Validée",
        type: "light_green",
      },
      {
        name: HabilitationValidationStatusEnum.Invalid,
        value: "Invalidée",
        type: "light_red",
      },
      {
        name: HabilitationValidationStatusEnum.Coming,
        value: "À venir",
        type: "light_blue",
      },
    ].find((x) => x.name === value);
  }

  function getHabilitationStatus(expirationDate: Date) {
    if (!expirationDate) {
      return HabilitationStatusEnum.COMING;
    } else if (isBefore(expirationDate, new Date())) {
      return HabilitationStatusEnum.EXPIRED;
    } else if (isBefore(expirationDate, add(new Date(), { months: 6 }))) {
      return HabilitationStatusEnum.EXPIRING_SOON;
    } else {
      return HabilitationStatusEnum.VALID;
    }
  }

  return {
    headers,
    footersActions,
    statusMapping,
    attendeeStatusMapping,
    getHabilitationStatus,
    renewalStatusMapping,
  };
};

export const useUsersHabilitationsValidation = () => {
  async function validateUsersHabilitationsCreation() {
    const createUserHabilitationStore = useCreateUserHabilitationsStore();
    const { user } = storeToRefs(createUserHabilitationStore);

    const mandatoryFieldMessage = "Ce champ est obligatoire";

    const rules = computed(() => {
      return {
        habilitationName: {
          required: helpers.withMessage(mandatoryFieldMessage, required),
        },
        durationYears: {
          required: helpers.withMessage(mandatoryFieldMessage, required),
          minValue: helpers.withMessage("La durée doit être d'au moins 1 an", minValue(1)),
        },
        userId: {
          required: helpers.withMessage(mandatoryFieldMessage, required),
        },
      };
    });
    const v$ = useVuelidate(rules, user);
    await v$.value.$validate();

    return v$;
  }

  async function validateUsersHabilitationsUpdate() {
    const updateUserHabilitationStore = useUpdateUserHabilitationsStore();
    const { userUpdate } = storeToRefs(updateUserHabilitationStore);

    const mandatoryFieldMessage = "Ce champ est obligatoire";

    const rules = computed(() => {
      return {
        habilitationName: {
          required: helpers.withMessage(mandatoryFieldMessage, required),
        },
        durationYears: {
          required: helpers.withMessage(mandatoryFieldMessage, required),
          minValue: helpers.withMessage("La durée doit être d'au moins 1 an", minValue(1)),
        },
        userId: {
          required: helpers.withMessage(mandatoryFieldMessage, required),
        },
      };
    });

    const v$ = useVuelidate(rules, userUpdate);
    await v$.value.$validate();

    return v$;
  }

  return {
    validateUsersHabilitationsCreation,
    validateUsersHabilitationsUpdate,
  };
};
